<template>
  <div>
    <Head title="Delivery Run" />
    <div class="flex items-center justify-between mb-6 mt-6 overflow-x-auto">
      <h1 class="mb-8 text-3xl font-bold">
        <Link class="text-orange-400 hover:text-orange-600" :href="`/orders/delivery-runs/invoicing-queue`">Orders</Link>
        <span class="text-orange-400 font-medium">/</span>{{ user.name }}/Orders/{{ order.order_no }}
      </h1>
      <div class="flex items-end justify-end space-x-3">
        <button
          v-show="order.status === 14 && invoicingActions"
          type="button" 
          class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-green sm:text-sm sm:leading-5"
          @click="invoiceOrderModel(order)"
        >
          <span>Invoice</span>
        </button>
        <button
          v-show="order.status === 14 && invoicingActions"
          type="button" 
          class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-red-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-red-500 focus:outline-none focus:border-red-700 focus:shadow-outline-red sm:text-sm sm:leading-5"
          @click="declineOrderModel(order)"
        >
          <span>Decline</span>
        </button>
        <button
          v-show="(is_my_order === 1 && (order.status === 10 || order.status === 11))"
          type="button" 
          class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-orange-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-orange-500 focus:outline-none focus:border-orange-700 focus:shadow-outline-orange sm:text-sm sm:leading-5"
          @click="returnOrderModel()"
        >
          <span>Return</span>
        </button>
        <span v-if="order.invoice && (order.status === 10 || order.status === 6)" class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
          <a
            class="btn btn-orange text-xs cursor-pointer ml-1 mr-1"
            :href="`/orders/${order.id}/pdf${pdfUrl()}`" 
            download
            target="__blank"
            title="Download"
          >
            <span>Download Invoice</span>
          </a>
        </span>
      </div>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <div>
        <form>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 md:px-6 sm:flex md:flex w-full">
            <h1 class="status">
              Status
              <span v-if="order_details.status === 'Declined'" class="declined"> [Declined][Reason: {{ order_details.decline_reason }}] </span>
              <span v-else class="approved"> [{{ order_details.status }}] </span>
            </h1>
          </div>
          <div class="bg-white px-4 pt-0 pb-4 sm:p-6 sm:pb-4">
            <div class="bg-orange-50 rounded-md shadow overflow-x-auto mb-4">
              <div class="grid grid-cols-1 gap-4 p-1">
                <div class="pt-2 p-2">
                  <h1 style="font-size:20px; font-weight: bold;">
                    Customer Details
                  </h1>
                </div>
              </div>
            </div>
            <div class="mb-4">
              <table width="50%">
                <tr>
                  <td class="pl-1 pb-1 pt-1"><strong>Customer</strong></td>
                  <td>: {{ customer.name }}</td>
                </tr>
                <tr>
                  <td class="pl-1 pb-1 pt-1"><strong>Channel</strong></td>
                  <td>: {{ customer.channel }}</td>
                </tr>
                <tr>
                  <td class="pl-1 pb-1 pt-1"><strong>Region</strong></td>
                  <td>: {{ customer.region }}</td>
                </tr>
                <tr>
                  <td class="pl-1 pb-1 pt-1"><strong>City</strong></td>
                  <td>: {{ customer.city }}</td>
                </tr>
                <tr>
                  <td class="pl-1 pb-1 pt-1"><strong>Contact</strong></td>
                  <td>: {{ customer.phone }} {{ customer.alternative_phone ? `, ${customer.alternative_phone}` : '' }}</td>
                </tr>
              </table>
            </div>
            <div class="bg-orange-50 rounded-md shadow overflow-x-auto mb-4">
              <div class="grid grid-cols-1 gap-4 p-1">
                <div class="pt-2 p-2">
                  <h1 style="font-size:20px; font-weight: bold;">
                    Ordered Details
                  </h1>
                </div>
              </div>
            </div>
            <div class="mb-4 overflow-x-auto">
              <table class="w-full whitespace-nowrap">
                <tr class="text-left font-bold">
                  <th class="pb-3 pt-3 px-3">#</th>
                  <th class="pb-3 pt-3 px-3">SKU</th>
                  <th class="pb-3 pt-3 px-3">Description</th>
                  <th class="pb-3 pt-3 px-3">Quantity</th>
                  <th v-show="order_details.status === 'Returned'" class="pb-3 pt-3 px-3">Quantity Returned</th>
                  <th class="pb-3 pt-3 px-3">Unit Price</th>
                  <th class="pb-3 pt-3 px-3">Discount (%)</th>
                  <th class="pb-3 pt-3 px-3 justify-end float-right">Amount</th>
                </tr>
                <tr v-for="(item, index) in order_details.details" :key="index">
                  <td class="w-px border-t pb-3 pt-3" width="2%">
                    <span class="flex items-center px-3">{{ index+1 }}</span>
                  </td>
                  <td class="w-px border-t pb-3 pt-3" width="10%">
                    <span class="flex items-left px-3">{{ item.sku }}</span>
                  </td>
                  <td class="w-px border-t pb-3 pt-3" style="word-wrap: break-word !important; word-break: break-all !important; white-space: normal !important; width: 40%;">
                    <span class="flex items-left px-3">{{ item.description }}</span>
                  </td>
                  <td class="w-px border-t pb-3 pt-3" width="5%">
                    <span class="flex items-center px-3">{{ item.quantity_packed }}</span>
                  </td>
                  <td v-show="order_details.status === 'Returned'" class="w-px border-t pb-3 pt-3" width="5%">
                    <span class="flex items-center px-3">{{ item.quantity_delivered }}</span>
                  </td>
                  <td class="w-px border-t pb-3 pt-3" width="5%">
                    <span class="flex items-end px-3 items-end justify-end">{{ item.unit_cost }}</span>
                  </td>
                  <td class="w-px border-t pb-3 pt-3" width="5%">
                    <span class="flex items-center px-3">{{ item.discount }}</span>
                  </td>
                  <td class="w-px border-t pb-3 pt-3">
                    <span class="flex items-end px-3 items-end justify-end">{{ item.amount_packed }}</span>
                  </td>
                </tr>
                <tr v-if="order_details.delivery_fees && order_details.delivery_fees > 0">
                  <td class="w-px border-t pb-3 pt-3" width="2%"><span class="flex items-center px-3"> {{ order_details.details.length + 1 }}</span></td>
                  <td class="w-px border-t pb-3 pt-3" width="10%">
                    <span class="flex items-left px-3">{{ order_details.transport_name }}</span>
                  </td>
                  <td class="w-px border-t pb-3 pt-3" width="10%">
                    <span class="flex items-left px-3">{{ order_details.transport_description }}</span>
                  </td>
                  <td class="w-px border-t pb-3 pt-3" width="10%">
                    <span class="flex items-left px-3">1</span>
                  </td>
                  <td v-show="order_details.status === 'Returned'" class="w-px border-t pb-3 pt-3" width="5%">
                    <span class="flex items-center px-3"></span>
                  </td>
                  <td class="w-px border-t pb-3 pt-3">
                    <span class="flex items-end px-3 items-end justify-end">{{ order_details.transport_amount }}</span>
                  </td>                  
                  <td class="w-px border-t pb-3 pt-3">
                    <span class="flex items-end px-3">0</span>
                  </td>
                  <td class="w-px pb-3 pt-3  border-t border-b">
                    <span class="flex items-end px-3 items-end justify-end">{{ order_details.transport_amount }}</span>
                  </td>
                </tr>
                <tr v-if="order_details.voucherInfo && order_details.voucherInfo.code && order_details.has_discount_voucher == 0">
                  <td class="pb-3 pt-3  border-t border-b"><span class="flex items-center px-3">-</span></td>
                  <td class="pb-3 pt-3  border-t border-b" colspan="6"> Voucher Code: #{{ order_details.voucherInfo.code }} - {{ order_details.voucherInfo.value }} {{ order_details.voucherInfo.type === 'Percentage' ? '%' : '' }}</td>
                  <td class="pb-3 pt-3  border-t border-b">
                    <span class="flex items-end px-3 items-end justify-end">{{ order_details.voucherInfo.voucher_amount }}</span>
                  </td>
                </tr>
                <tr>
                  <th class="pb-3 pt-3  border-t border-b">Total</th>
                  <th class="pb-3 pt-3  border-t border-b" />
                  <th class="pb-3 pt-3  border-t border-b" />
                  <th class="pb-3 pt-3  border-t border-b" />
                  <th class="pb-3 pt-3  border-t border-b" />
                  <th v-show="order_details.status === 'Returned'" class="pb-3 pt-3  border-t border-b" />
                  <th class="pb-3 pt-3  border-t border-b" />
                  <th class="pb-3 pt-3  border-t border-b">
                    <span class="flex items-end px-3 items-end justify-end">{{ order_details.amount }}</span>
                  </th>
                </tr>
              </table>
            </div>
            <div class="bg-orange-50 rounded-md shadow overflow-x-auto mb-4">
              <div class="grid grid-cols-1 gap-4 p-1">
                <div class="pt-2 p-2">
                  <h1 style="font-size:20px; font-weight: bold;">
                    Payment Details
                  </h1>
                </div>
              </div>
            </div>
            <div v-if="order_details.payments.length > 0" class="mb-4 overflow-x-auto">
              <table class="w-full whitespace-nowrap">
                <tr class="text-left font-bold">
                  <th class="pb-3 pt-3 px-3">#</th>
                  <th class="pb-3 pt-3 px-3">Date</th>
                  <th class="pb-3 pt-3 px-3">Payment Mode</th>
                  <th class="pb-3 pt-3 px-3">Reference</th>                  
                  <th class="pb-3 pt-3 px-3">Initiated By</th>
                  <th class="pb-3 pt-3 px-3">Phone Number</th>
                  <th class="flex items-end px-3 items-end justify-end">Amount</th>
                </tr>
                <tr v-for="(item, index) in order_details.payments" :key="index">
                  <td class="w-px border-t pb-3 pt-3">
                    <span class="flex items-center px-3">{{ index+1 }}</span>
                  </td>
                  <td class="w-px border-t pb-3 pt-3">
                    <span class="flex items-left px-3">{{ item.date }}</span>
                  </td>
                  <td class="w-px border-t pb-3 pt-3">
                    <span class="flex items-left px-3">{{ item.provider }}</span>
                  </td>
                  <td 
                    class="w-px border-t pb-3 pt-3 break-all" 
                    style="word-wrap: break-word !important; word-break: break-all !important; white-space: normal !important; width: 30%;">
                    <p class="px-3">{{ item.reference }}</p>
                  </td>
                  <td class="w-px border-t pb-3 pt-3">
                    <span class="flex items-center px-3">{{ item.user }}</span>
                  </td>
                  <td class="w-px border-t pb-3 pt-3">
                    <span class="flex items-center px-3">{{ item.phone_number }}</span>
                  </td>
                  <td class="w-px border-t pb-3 pt-3">
                    <span class="flex items-end px-3 items-end justify-end">{{ item.amount }}</span>
                  </td>                  
                </tr>
                <tr>
                  <th class="pb-3 pt-3  border-t border-b">Total</th>
                  <th class="pb-3 pt-3  border-t border-b" />
                  <th class="pb-3 pt-3  border-t border-b" />
                  <th class="pb-3 pt-3  border-t border-b" />
                  <th class="pb-3 pt-3  border-t border-b" />
                  <th class="pb-3 pt-3  border-t border-b" />
                  <th class="pb-3 pt-3  border-t border-b">
                    <span class="flex items-end px-3 items-end justify-end">{{ order_details.total_payments }}</span>
                  </th>
                </tr>
              </table>              
            </div>
            <div class="bg-orange-50 rounded-md shadow overflow-x-auto mb-4">
              <div class="grid grid-cols-1 gap-4 p-1">
                <div class="pt-2 p-2">
                  <h1 style="font-size:20px; font-weight: bold;">
                    Notes
                  </h1>
                </div>
              </div>
            </div>
            <div class="mb-4">
              <p v-show="order_details.notes">{{ order_details.notes }}</p>
            </div>
          </div>
        </form>
      </div>
    </div>
    <div class="px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
      <div class="flex items-end justify-end space-x-3">
        <button
          v-show="order.status === 14 && invoicingActions"
          type="button" 
          class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-green sm:text-sm sm:leading-5"
          @click="invoiceOrderModel(order)"
        >
          <span>Invoice</span>
        </button>
        <button
          v-show="order.status === 14 && invoicingActions"
          type="button" 
          class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-red-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-red-500 focus:outline-none focus:border-red-700 focus:shadow-outline-red sm:text-sm sm:leading-5"
          @click="declineOrderModel(order)"
        >
          <span>Decline</span>
        </button>
        <button
          v-show="(is_my_order === 1 && (order.status === 10 || order.status === 11))"
          type="button" 
          class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-orange-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-orange-500 focus:outline-none focus:border-orange-700 focus:shadow-outline-orange sm:text-sm sm:leading-5"
          @click="returnOrderModel()"
        >
          <span>Return</span>
        </button>
        <span v-if="order.invoice && (order.status === 10 || order.status === 6)" class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
          <a
            class="btn btn-orange text-xs cursor-pointer ml-1 mr-1"
            :href="`/orders/${order.id}/pdf${pdfUrl()}`" 
            download
            target="__blank"
            title="Download"
          >
            <span>Download Invoice</span>
          </a>
        </span>
      </div>
    </div>
  </div>
  <!--modal content-->
  <div v-if="isInvoiceOpen" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              You are about to invoice order number <strong>{{ order.order_no }}</strong>. Do you wish to continue?
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-orange-500 text-base leading-6 font-medium text-white shadow-sm hover:bg-orange-500 focus:outline-none focus:border-orange-700 focus:shadow-outline-orange sm:text-sm sm:leading-5" @click="postOrder(order, 'invoice')">
                Yes
              </button>
            </span>
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5" @click="closeModal">
                No
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
  <!--modal content-->
  <div v-if="isDeclineOpen" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              You are about to decline order number <strong>{{ order.order_no }}</strong>. Do you wish to continue?
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-orange-500 text-base leading-6 font-medium text-white shadow-sm hover:bg-orange-500 focus:outline-none focus:border-orange-700 focus:shadow-outline-orange sm:text-sm sm:leading-5" @click="postOrder(order, 'decline')">
                Yes
              </button>
            </span>
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5" @click="closeDeclineModal">
                No
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
  <div v-if="isOpen" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex flex-wrap w-full items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-2 sm:align-middle" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 md:px-6 sm:flex md:flex modal-wrapper w-full">
            <h1>Return an Order</h1>
          </div>
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              <div
                v-show="(is_my_order === 1 && (order.status === 10 || order.status === 11))"
                class="w-full"
              >
                <div
                  v-for="(partial, index) in form.orders"
                  :key="index"
                  class="flex flex-wrap w-full"
                >
                  <div class="pb-2 pr-6 w-full lg:w-full">
                    <text-input
                      :key="index"
                      v-model="partial.quantity"
                      :error="showValidationErrors(`orders.${index}.quantity`)"
                      placeholder="Quantity"
                      :label="`${partial.label}`"
                    />
                  </div>
                  <div class="pb-2 pr-6 w-full lg:w-full">
                    <text-input
                      :key="index"
                      v-model="partial.id"
                      :error="form.errors.id"
                      class="pb-2 hidden"
                      placeholder="ID"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-orange-500 text-base leading-6 font-medium text-white shadow-sm hover:bg-orange-500 focus:outline-none focus:border-orange-700 focus:shadow-outline-orange sm:text-sm sm:leading-5" @click="returnOrder()">
                Yes
              </button>
            </span>
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5" @click="closeModal">
                Cancel
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
  <div v-if="isMissing" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex flex-wrap w-full items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-2 sm:align-middle" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 md:px-6 sm:flex md:flex modal-wrapper w-full">
            <h1>Missing Invoice</h1>
          </div>
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              <div
                class="w-full"
              >
                This information is not available, please contact your administrator.
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5" @click="closeModal">
                Close
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
      
<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import Pagination from '@/Shared/Pagination'
import TextInput from '@/Shared/TextInput'
      
export default {
  components: {
    Head,
    Link,
    SearchFilter,
    Pagination,
    TextInput,
  },
  layout: Layout,
  props: {
    filters: Object,
    order: Object,
    order_details: Object,
    user: Object,
    customer: Object,
    delivery_run: Object,
    orders: Object,
    is_my_order: Number,
    voucherInfo: Object,
    delivery_fees: Object,
    payments: Object,
    voucherAmount: Object,
  },
  data() {
    return {
      form: this.$inertia.form({
        orders: this.orders,
        order_id: this.order.id,
      }),
      search: {
        search: this.filters.search,
      },
      isInvoiceOpen: false,
      isDeclineOpen: false,
      isOpen: false,
      isMissing: false,
      invoicingActions: false,
    }
  },
  watch: {
    search: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get(`/orders/delivery-runs/invoicing-queue/users/${this.user.username}/orders/${this.order.id}/skus`, pickBy(this.search), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.search = mapValues(this.search, () => null)
    },
    postOrder(order, status) {
      const data = {}
      data.order_id = order.id
      data.status = status
      data._method = 'POST'
      this.$inertia.post(`/delivery-runs/${this.delivery_run.id}/orders/${this.order.id}/invoicing-queue`, data, {
        onSuccess: () => {
          this.isInvoiceOpen = false
          this.isDeclineOpen = false
          this.isOpen = false
          this.isMissing = false
        },
      })
    },
    invoiceOrderModel() {
      this.isInvoiceOpen = true
      this.isDeclineOpen = false
      this.isOpen = false
      this.isMissing = false
    },
    returnOrderModel() {
      this.isInvoiceOpen = false
      this.isDeclineOpen = false
      this.isOpen = true
    },
    closeModal() {
      this.isInvoiceOpen = false
      this.isDeclineOpen = false
      this.isOpen = false
      this.isMissing = false
    },
    declineOrderModel() {
      this.isDeclineOpen = true
      this.isInvoiceOpen = false
      this.isOpen = false
      this.isMissing = false
    },
    closeDeclineModal() {
      this.isDeclineOpen = false
      this.isInvoiceOpen = false
      this.isOpen = false
      this.isMissing = false
    },
    isValidationErrors(value) {
      if (this.form.errors) {
        const error = this.form.errors[value] || ''
        return !!error
      }
      return false
    },
    showValidationErrors(value) {
      if (this.form.errors) {
        const error = this.form.errors[value] || ''
        return error || ''
      }
      return ''
    },
    returnOrder() {
      this.form.post(`/orders/${this.order.id}/skus/return`, {
        onSuccess: () => {
          this.form.reset(
            'orders',
          )
          this.isOpen = false
          this.selectedCancellationReason = null
        },
      })      
    },
    pdfUrl() {
      let query = ''
      if (this.order) {
        query += `oid=${this.order.id}`
      }
      if (query) {
        query = `?${query}`
      }
      return query
    },
  },
}
</script>
<style>
  .approved {
    color: green;
  }
  .declined {
    color: red;
  }
  .status {
    font-size: 20px;
    font-weight:bold;
    margin-bottom: 10px;
    margin-top: 10px;
  }
</style>
      